import { useState, type ReactNode, type SyntheticEvent } from "react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";

import { useAuthConfig } from "../api/queries";
import { RpcError, postAuth } from "../api/rpc";
import type { AuthProviderOption } from "../api/types";
import { useAuth } from "../auth/auth-context";
import { cx } from "../lib/cx";
import { browserNavigation } from "../lib/navigation";
import { StorageKey, readStored, writeStored } from "../lib/storage";
import { Button } from "../ui/Button";

function errorMessage(cause: unknown): string {
  if (cause instanceof RpcError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return "Something went wrong. Try again.";
}

interface LocationState {
  from?: string;
}

export function LoginScreen({ mode = "signin" }: { mode?: "signin" | "create" }): ReactNode {
  const { status } = useAuth();
  const config = useAuthConfig();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState("");
  const [devName, setDevName] = useState("");
  const [sent, setSent] = useState(false);
  const [pending, setPending] = useState<"email" | "dev" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastProvider, setLastProvider] = useState<string | null>(() =>
    readStored(StorageKey.lastProvider),
  );

  if (status === "authenticated") {
    const state = location.state as LocationState | null;
    return <Navigate to={state?.from ?? "/home"} replace />;
  }

  const providers: AuthProviderOption[] = config.data?.providers ?? [];
  const devProvider = providers.find((provider) => provider.id === "dev");
  const emailProvider = providers.find((provider) => provider.id === "email");
  // `email` and `dev` are posted to, not redirected to, so they get forms of
  // their own rather than a link in the provider list.
  const ssoProviders = providers.filter(
    (provider) => provider.id !== "dev" && provider.id !== "email",
  );

  const remember = (id: string): void => {
    writeStored(StorageKey.lastProvider, id);
    setLastProvider(id);
  };

  const submitEmail = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setPending("email");
    try {
      const result = await postAuth("/auth/email", { email });
      remember("email");
      // A user backed by SSO is steered to their provider instead of being mailed.
      if (result.redirect !== undefined) {
        browserNavigation.assign(result.redirect);
        return;
      }
      setSent(true);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(null);
    }
  };

  const submitDev = async (event: SyntheticEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setPending("dev");
    try {
      const result = await postAuth("/auth/dev", { email, name: devName });
      remember("dev");
      browserNavigation.assign(result.redirect ?? "/home");
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(null);
    }
  };

  const notice = searchParams.get("notice");
  const heading =
    mode === "create" ? "Create a workspace" : (config.data?.name ?? "Sign in to your workspace");

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12 text-ink">
      <div className="w-full max-w-sm">
        <h1 className="mb-1 text-2xl font-semibold">{heading}</h1>
        <p className="mb-6 text-sm text-muted">
          {mode === "create"
            ? "Choose how you want to sign in to set up the new workspace."
            : "Choose a sign-in method to continue."}
        </p>

        {notice !== null && (
          <p className="mb-4 rounded-md border border-line bg-surface px-3 py-2 text-sm">
            {notice}
          </p>
        )}

        {config.isError && (
          <p role="alert" className="mb-4 text-sm text-danger">
            {config.error.message}
          </p>
        )}
        {error !== null && (
          <p role="alert" className="mb-4 text-sm text-danger">
            {error}
          </p>
        )}

        {config.isPending ? (
          <p className="text-sm text-muted" role="status">
            Loading sign-in options…
          </p>
        ) : (
          <>
            {ssoProviders.length > 0 && (
              <ul className="mb-6 flex flex-col gap-2">
                {ssoProviders.map((provider) => (
                  <li key={provider.id}>
                    <a
                      href={provider.authUrl}
                      onClick={() => {
                        remember(provider.id);
                      }}
                      className={cx(
                        "flex h-10 items-center justify-center gap-2 rounded-md border text-sm font-medium",
                        lastProvider === provider.id
                          ? "border-accent bg-surface"
                          : "border-line hover:bg-hover",
                      )}
                    >
                      Continue with {provider.name}
                      {lastProvider === provider.id && (
                        <span className="text-xs text-muted">Last used</span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {emailProvider === undefined ? null : sent ? (
              <div role="status" className="rounded-md border border-line bg-surface p-4 text-sm">
                <p className="font-medium">Check your email</p>
                <p className="mt-1 text-muted">
                  If an account exists for {email}, a sign-in link is on its way.
                </p>
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  void submitEmail(event);
                }}
                className="flex flex-col gap-2"
                aria-label="Sign in with email"
              >
                <label className="flex flex-col gap-1 text-sm">
                  Email
                  <input
                    type="email"
                    name="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
                  />
                </label>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={pending === "email"}
                  className={cx(lastProvider === "email" && "ring-2 ring-accent")}
                >
                  {pending === "email" ? "Sending…" : "Continue with email"}
                </Button>
              </form>
            )}

            {devProvider !== undefined && (
              <form
                onSubmit={(event) => {
                  void submitDev(event);
                }}
                aria-label="Development sign-in"
                className="mt-8 flex flex-col gap-2 rounded-md border border-dashed border-line p-4"
              >
                <p className="text-xs font-semibold tracking-wide text-muted uppercase">
                  Development sign-in
                </p>
                <label className="flex flex-col gap-1 text-sm">
                  Email
                  <input
                    type="email"
                    name="devEmail"
                    required
                    value={email}
                    onChange={(event) => {
                      setEmail(event.target.value);
                    }}
                    className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm">
                  Name
                  <input
                    type="text"
                    name="devName"
                    required
                    value={devName}
                    onChange={(event) => {
                      setDevName(event.target.value);
                    }}
                    className="rounded-md border border-line bg-raised px-3 py-2 text-sm"
                  />
                </label>
                <Button type="submit" disabled={pending === "dev"}>
                  {pending === "dev" ? "Signing in…" : "Sign in as developer"}
                </Button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
